import { TFunction } from 'i18next';

/**
 * What a vendor can do to a finished photo, in their words. The card menu and
 * the inspector offer the same list.
 */

export type EditorTool = 'retouch' | 'modify' | 'expand' | 'graphics';

export type AnglePreset = 'turn-left' | 'turn-right' | 'higher' | 'lower' | 'closer' | 'wider';

/** The angle tool turns from the current view: 0° is the picture as it is. */
export const ANGLE_PRESETS: Record<AnglePreset, { azimuth: number; elevation: number; distance: number }> = {
    'turn-left': { azimuth: 315, elevation: 0, distance: 5 },
    'turn-right': { azimuth: 45, elevation: 0, distance: 5 },
    higher: { azimuth: 0, elevation: 30, distance: 5 },
    lower: { azimuth: 0, elevation: -20, distance: 5 },
    closer: { azimuth: 0, elevation: 0, distance: 2 },
    wider: { azimuth: 0, elevation: 0, distance: 8 },
};

export function angleRequest(preset: AnglePreset) {
    return { recipeSlug: 'style-variation', inputs: { mode: 'change-angle', ...ANGLE_PRESETS[preset] } };
}

export function editorToolLabel(t: TFunction, tool: EditorTool): { label: string; hint: string } {
    switch (tool) {
        case 'retouch': return { label: t('STUDIO.EDIT.RETOUCH', 'Retouch an area'), hint: t('STUDIO.EDIT.RETOUCH_HINT', 'Circle something and say what to do with it') };
        case 'modify': return { label: t('STUDIO.EDIT.MODIFY', 'Change with a prompt'), hint: t('STUDIO.EDIT.MODIFY_HINT', '“Make it evening”, “add a plant on the left”') };
        case 'expand': return { label: t('STUDIO.EDIT.EXPAND', 'Expand the frame'), hint: t('STUDIO.EDIT.EXPAND_HINT', 'Show more around the picture') };
        case 'graphics': return { label: t('STUDIO.EDIT.GRAPHICS', 'Add text and arrows'), hint: t('STUDIO.EDIT.GRAPHICS_HINT', 'Labels and callouts drawn into the photo') };
        default: return { label: tool, hint: '' };
    }
}

export function angleLabel(t: TFunction, p: AnglePreset): string {
    switch (p) {
        case 'turn-left': return t('STUDIO.ANGLE.LEFT', 'Turn to the left');
        case 'turn-right': return t('STUDIO.ANGLE.RIGHT', 'Turn to the right');
        case 'higher': return t('STUDIO.ANGLE.HIGHER', 'From higher up');
        case 'lower': return t('STUDIO.ANGLE.LOWER', 'From lower down');
        case 'closer': return t('STUDIO.ANGLE.CLOSER', 'Move closer');
        case 'wider': return t('STUDIO.ANGLE.WIDER', 'Step back');
        default: return p;
    }
}

// Graphics (text and arrows painted in) is retired: words go on the film as overlays.
export const EDITOR_TOOLS: EditorTool[] = ['retouch', 'modify', 'expand'];
export const ANGLE_LIST: AnglePreset[] = ['turn-left', 'turn-right', 'higher', 'lower', 'closer', 'wider'];
